import React, { createContext, useContext, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { apiPatch, useAxiosRQ } from "../api/apiREST";

type UserDataContextValue = {
  profile: any | null;
  profileLoading: boolean;
  profileError: any;
  refetchProfile: () => Promise<any>;

  tokens: any | null;
  tokensLoading: boolean;
  tokensError: any;
  refetchTokens: () => Promise<any>;

  subscriptions: any | null;
  subscriptionsLoading: boolean;
  subscriptionsError: any;
  refetchSubscriptions: () => Promise<any>;

  refreshAll: () => Promise<void>;
  updateProfile: (data: any) => Promise<{ data: any; error: any }>;
};

const UserDataContext = createContext<UserDataContextValue | null>(null);

export function UserDataProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const emptyParams = useMemo(() => ({}), []);

  const hasAuthToken = Boolean(localStorage.getItem("access_token"));

  const profileQuery = useAxiosRQ("auth/users/me/", "user-profile", emptyParams, {
    enabled: hasAuthToken,
    retry: false,
  });

  const tokensQuery = useAxiosRQ(
    "/customers/tokens/",
    "user-tokens",
    emptyParams,
    {
      enabled: hasAuthToken,
      retry: false,
    }
  );

  const subscriptionsQuery = useAxiosRQ(
    "/subscriptions/my_subscriptions/",
    "user-subscriptions",
    emptyParams,
    {
      enabled: hasAuthToken,
      retry: false,
    }
  );

  const updateProfile = async (data: any) => {
    const response = await apiPatch("auth/users/me/", data);
    if (response?.data) {
      // Keep both the provider's query and any legacy "profile" queryKey in sync.
      queryClient.setQueryData(["user-profile", emptyParams], response.data);
      queryClient.setQueryData(["profile", emptyParams], response.data);
    }
    return { data: response?.data ?? null, error: response?.error ?? null };
  };

  const value: UserDataContextValue = {
    profile: profileQuery.data ?? null,
    profileLoading: profileQuery.isLoading,
    profileError: profileQuery.error,
    refetchProfile: profileQuery.refetch,

    tokens: tokensQuery.data ?? null,
    tokensLoading: tokensQuery.isLoading || tokensQuery.isFetching,
    tokensError: tokensQuery.error,
    refetchTokens: tokensQuery.refetch,

    subscriptions: subscriptionsQuery.data ?? null,
    subscriptionsLoading:
      subscriptionsQuery.isLoading || subscriptionsQuery.isFetching,
    subscriptionsError: subscriptionsQuery.error,
    refetchSubscriptions: subscriptionsQuery.refetch,

    refreshAll: async () => {
      await Promise.allSettled([
        profileQuery.refetch(),
        tokensQuery.refetch(),
        subscriptionsQuery.refetch(),
      ]);
    },
    updateProfile,
  };

  return (
    <UserDataContext.Provider value={value}>{children}</UserDataContext.Provider>
  );
}

export function useUserData() {
  const ctx = useContext(UserDataContext);
  if (!ctx) {
    throw new Error("useUserData must be used within a UserDataProvider");
  }
  return ctx;
}




